import ExecuteDocfinityDatasourceDS from "common/datasources/document/executeDocFinityDatasource";
import MacroOps from "common/datasources/macros/MacroOperations";
import DefaultValueApply from "common/widgets/PromptList/DefaultValueApply";
import PromptInputType from "common/enums/PromptInputType";
import PromptDefaultValueType from "common/enums/dashboards/PromptDefaultValueType";
import ObjectUtil from "common/helper/ObjectUtil";
// ---------- Handle dependent prompts. Change handlers on all prompt types call
// these functions to first determine if there are other prompts whose values
// they are responsible for.  This comes back from the server, and is stored in
// "responsibilityMapping".  If non-empty, the datasources or macros on the prompts this
// one is responsible for are re-run, and the prompts' values are re-populated.
//
// A prompt can be populated by either a datasource or a macro (static macro, not macro datasource
// though both are possible).  The useDatasource boolean tells us whether to evaluate the
// datasource (with given datasourceId) or the macro (with given macroString).

export default kendo.Class.extend({

    /**
     * This class will keep all prompt values up to date.
     * @param dataSource prompt
     * @constructor
     */
    init: function(dataSource) {
        this.dataSource = dataSource;
        this.changed = [];
        this._inProgress = false;
        this._allowQueue = true;
        var me = this;
        this.change = function(e) {
            if (e && e.action === "itemchange" && e.field === "value") {
                me._handleChange(e.items[0]);
            }
        };
        this.dataSource.bind("change", this.change);
    },

    /**
     * Stop changing the datasource.
     */
    destroy: function() {
        if (this.dataSource) {
            this.dataSource.unbind("change", this.change);
            delete this.dataSource;
            delete this.change;
            delete this.changed;
        }
    },

    /**
     * Handle the change in a prompt.
     * @param prompt Prompt
     */
    _handleChange: function(prompt) {
        if (this._allowQueue && (this.changed.length === 0 || this.changed[this.changed.length - 1] !== prompt)) {
            //No reason to execute the same dependency twice in a row
            //This is useful for a diamond dependency
            this.changed.push(prompt);
            this._process();
        }
    },

    /**
     * Apply batch changes to the datasource.  Dependencies will be executed after all changes have been made.
     * @param func Function that applies the batch changes.
     * @returns {object} Returns a promise that resolves when all dependencies have been executed.
     */
    batch: function(func) {
        this._inProgress = true;
        func();
        this._inProgress = false;
        return this._process();
    },

    /**
     * Allow applying changes to the underlying datasource without running dependencies.
     *
     * @param func function that applies the changes to the datasource
     */
    ignore: function(func) {
        this._allowQueue = false;
        func();
        this._allowQueue = true;
    },

    /**
     * Attempt to process a single change event.
     * @private
     */
    _process: function() {
        if (!this._inProgress && this.changed) {
            var prompt = this.changed.shift();
            if (!this._processDeferred) {
                this._processDeferred = $.Deferred();
            }
            var def = this._processDeferred;
            if (prompt) {
                this._inProgress = true;
                var me = this;
                this._handlePromptChange(prompt)
                    .always(function() {
                        me._inProgress = false;
                        me._process();
                    });
            } else {
                delete this._processDeferred;
                def.resolve();
            }
        }
        return this.getFinished();
    },

    /**
     * Get a promise that will be resolved when executing dependencies is in a finished state.
     */
    getFinished: function() {
        if (this._processDeferred) {
            return this._processDeferred.promise();
        } else {
            return $.Deferred().resolve().promise();
        }
    },

    /**
     * Get the prompt for a given name. It's possible it won't be found, if the server returns prompts in a responsibility mapping
     * that are not actually found, due to being on other steps of a form for example.
     * @param name Name of prompt
     * @returns {*} The prompt or null
     */
    _getPromptForName: function(name) {
        var prompts = this._getPrompts();
        for (var i = 0; i < prompts.length; i++) {
            if (prompts[i].name === name) {
                return prompts[i];
            }
        }
        return null;
    },

    _getPrompts: function() {
        return this.dataSource.view();
    },

    /**
     * Return arguments that can be used to run a prompt.  Returns false if some of the arguments are not satisfied.
     * @param promptToRun Prompt object to run
     */
    _getArgsToRunPrompt: function(promptToRun) {
        var me = this;
        // Determine whether or not this prompt can be re-calculated.
        // If it expects input from prompts with empty values, it can not be run.
        const missing = [];

        // Loop through arguments accepted by the datasource or macro, needed to run. Get values
        // for all arguments, by querying prompts for their current values
        // where needed.
        const args = (promptToRun?.parameterPromptDatasourceArguments || []).map(function(arg) {
            const arrArgDto = {};
            arrArgDto.name = arg.datasourceArgumentName;

            if (arg.argumentType === "STATIC" || arg.argumentType === "MACRO") {
                arrArgDto.dataType = "STRING";
                arrArgDto.value = arg.value;
            } else {

                // determine whether we are dealing with a .key argument name (indexing).
                var promptForArg,
                    field,
                    keyExpression = /^(.+)\.key$/,
                    match = keyExpression.exec(arg.value);
                if (match) {
                    promptForArg = me._getPromptForName(match[1]);
                    field = "key";
                } else {
                    promptForArg = me._getPromptForName(arg.value);
                    field = "value";
                }

                if (promptForArg != null) {
                    arrArgDto.dataType = promptForArg.dataType;
                    arrArgDto.value = promptForArg[field];
                }
            }
            // Do not allow datasource or macro execution with empty values
            if (!promptToRun.datasourceHasOptionalVars && (arrArgDto.value == null || arrArgDto.value.toString().length === 0)) {
                missing.push(arrArgDto.name)
            } else if (ObjectUtil.isArray(arrArgDto.value)) {
                if(arrArgDto.dataType === "STRING_ARRAY") {
                    arrArgDto.arrayValues = Array.from(arrArgDto.value);
                    delete arrArgDto.value;
                } else {
                    arrArgDto.value = arrArgDto.value.join(ObjectUtil.arrayDelimiter());
                }
            }
            return arrArgDto;
        });
        if (missing.length === 0) {
            return {
                valid: true,
                args,
                missing: []
            };
        } else {
            return {
                valid: false,
                missing
            }
        }
    },

    getMissingDependencies(prompt) {
        return this._getArgsToRunPrompt(prompt).missing;
    },

    /**
     * Return arguments that can be used to run a prompt.  Always returns an array of args, though it may be empty.
     * When evaluating macros, we allow empty values to be passed, and still attempt execution, contrary to running datasources.
     * @param prompts All prompts
     * @param macroString use the macroString to calculate variables
     */
    _getMacroArgs: function(macroString) {
        var me = this;
        var promptValues = [];
        if (macroString) {
            var matches = macroString.match(/\$\{[^\}]*\}/g);
            $.each(matches, function(i, val) {
                promptValues.push({
                    datasourceArgumentName: val.slice(2, -1)
                });
            });
        }

        // Loop through arguments accepted by the macro, needed to run. Get values
        // for all arguments, by querying prompts for their current values
        // where needed.
        return promptValues.map(function(arg) {
            var arrArgDto = {};
            arrArgDto.name = arg.datasourceArgumentName;

            var promptForArg = me._getPromptForName(arg.datasourceArgumentName);
            if (promptForArg != null) {
                arrArgDto.dataType = promptForArg.dataType;
                arrArgDto.value = promptForArg.getValue();
            }
            if (arrArgDto.value !== null && arrArgDto.value !== undefined && arrArgDto.value.toString().length > 0) {
                return arrArgDto;
            }
        });
    },

    /**
     * Set the data on a single prompt.
     * @param prompt Single prompt to set data on
     * @param data Array of data to set
     */
    _setData: function(prompt, data) {
        // before setting data, check for an edge case with multi-value string metadata and 1 item in the list.
        if (prompt.inputType === PromptInputType.DYNAMIC_FIELD && prompt.allowMultipleValues && data && data.length === 1) {
            prompt.set("singleMultiValue", true);
        }

        prompt.set("data", data);
        const emptyValue = prompt.inputType === PromptInputType.LIST_VIEW ? undefined : "";

        if (prompt.inputType === PromptInputType.LIST || prompt.inputType === PromptInputType.LIST_VIEW ||
            (prompt.inputType === PromptInputType.DYNAMIC_FIELD && !prompt.allowMultipleValues && data && data.length > 1)) {
            switch (prompt.defaultValueType) {
                case PromptDefaultValueType.FIRST:
                    if (data && data.length > 0) {
                        prompt.set("value", DefaultValueApply.convertValue(prompt, data[0].value));
                    } else {
                        prompt.set('value', emptyValue);
                    }
                    break;
                case PromptDefaultValueType.STATIC:
                case PromptDefaultValueType.SPECIFIC:
                    var sVal = prompt.get("staticDefaultValue");
                    if (prompt.canBeValue(sVal)) {
                        prompt.set("value", DefaultValueApply.convertValue(prompt, sVal));
                    } else {
                        prompt.set("value", emptyValue);
                    }
                    break;
                case PromptDefaultValueType.NONE:
                default:
                    //MACRO, DATASOURCE, PROMPT currently end up here. No need for them on the client side right now
                    prompt.set("value", emptyValue);
            }
        } else if (prompt.inputType === PromptInputType.DYNAMIC_FIELD && prompt.allowMultipleValues === true) {
            // don't set value in this case, we've previously set the renderAsListField.
            // 45337: multiple value mdo (ManyInput) expects an array, not an empty string
            if (data && data.length < 1) {
                prompt.set("value", []);
            } else if (data && data.length === 1 && data[0].value !== undefined) {
                // if the field allows multiple values but the result only returned a single value a string input is rendered
                prompt.set("value", data[0].value);
            } else if(prompt.originallyReadOnly) {
                prompt.set("value", (data || []).map((part) => part.value));
            } else if(prompt.value) {
                // if the dependency changes, ensure the value doesn't contain cached values from the previous result
                prompt.set("value", (data || [])
                    .filter((part) => prompt.get("value") === part.value || prompt.get("value").indexOf(part.value) >= 0)
                    .map((part) => part.value)
                );
            }
        } else if (data && data.length > 0 && data[0].value !== undefined) {
            prompt.set("value", data[0].value);
        } else {
            prompt.set("value", "");
        }
    },

    /**
     * Run a single prompt
     * @param promptName Name of the prompt to run
     * @returns {*} A promise that is resolved only when the prompt's value is done calculating
     */
    _runPrompt: function(promptName) {
        var me = this;
        var promptToRun = this._getPromptForName(promptName);

        // If the current prompt is responsible for this prompt in the loop, then re-execute it.
        // Only applicable if it has a datasource or macro with arguments -
        // (but it should never have has someone be responsible
        // for it, without either a datasource or macro with arguments being configured).
        if (promptToRun && promptToRun.parameterPromptDatasourceArguments != null) {

            var runData = this._getArgsToRunPrompt(promptToRun);

            // If the datasource or macro can be run (because all values are non-null), execute it now.
            if (runData.valid !== false) {
                if (promptToRun.datasourceId != null && promptToRun.datasourceId !== "") {
                    // set a loading flag on the prompt
                    this._setPromptLoading(promptToRun, true);
                    promptToRun.set("datasourceExecuted", false);
                    return this._executeDatasource(promptToRun, runData.args)
                        .done(function(data) {
                            promptToRun.set("datasourceExecuted", true);
                            me._setData(promptToRun, data);
                        })
                        .always(function() {
                            me._setPromptLoading(promptToRun, false);
                        });
                } else if (promptToRun.macroString != null && promptToRun.macroString !== "") {
                    this._setPromptLoading(promptToRun, true);
                    return MacroOps.execute([{
                            macro: promptToRun.macroString,
                            arguments: runData.args
                        }])
                        .done(function(data) {
                            if (data[0].successCount) {
                                me._setData(promptToRun, [{
                                    value: data[0].successful[0]
                                }]);
                            }
                        })
                        .always(function() {
                            me._setPromptLoading(promptToRun, false);
                        });
                }
            } else {
                // if the datasource or macro cannot be run because one or more dependencies does not have a value,
                // we need to clear the data/value for this prompt.  Leaving it would allow stale data to be entered.
                this._setData(promptToRun, []);
            }
        }

        //Nothing was executed, so this is done right away
        return $.Deferred().resolve().promise();
    },

    /**
     * Execute the datasource for the prompt
     * @param prompt {*} The prompt being executed
     * @param args {Array} Arguments needed by the datasource to execute
     * @returns {*}
     * @private
     */
    _executeDatasource: function(prompt, args) {
        return ExecuteDocfinityDatasourceDS.executeDocfinityDatasource(undefined, prompt.datasourceId, args);
    },

    /**
     * Sets the loading state of the prompt and keeps a count so loading isn't done until all requests are done
     * @param prompt {object} The prompt that is changing
     * @param isLoading {boolean} Whether the prompt is loading or not
     */
    _setPromptLoading: function(prompt, isLoading) {
        if (isLoading === true) {
            prompt.set("loading", true);
            prompt.set("loadingCount", (prompt.get("loadingCount") || 0) + 1);
        } else {
            prompt.set("loadingCount", prompt.get("loadingCount") - 1);
            prompt.set("loading", prompt.get("loadingCount") > 0);
        }
    },

    /**
     * Run a single prompt
     * @param responsibilities The responsibilityMapping field's array, with objects with name and field.
     * @param prompts ALl prompts
     * @returns {*} A promise that is resolved only when the prompt's value is done calculating
     */
    _changePromptBinding: function(responsibilities) {
        var me = this;
        var macroObjects = [];
        //Run all prompts return an array of promises that resolve when each prompt's responsibility resolves
        responsibilities.forEach(function(val) {
            // We moved to responsibilityMapping supporting a {name:[name],field:'VALUE'||'REQUIRED'||'DISABLED'||'HIDDEN'} structure
            if (val.field !== "VALUE") {
                // It's possible to not find the prompt, if it's not on this step.
                var promptToRun = me._getPromptForName(val.name);

                // If the current prompt is responsible for this prompt in the loop, then re-execute it.
                // Only applicable if it has a macro with arguments AND is on the current step -
                // (but it should never have has someone be responsible
                // for it, without a macro with arguments being configured).
                if (promptToRun) {
                    var macroString;
                    switch (val.field) {
                        case "HIDDEN":
                            macroString = promptToRun.hiddenMacro;
                            break;
                        case "REQUIRED":
                            macroString = promptToRun.requiredMacro;
                            break;
                        case "DISABLED":
                            macroString = promptToRun.disabledMacro;
                            break;
                    }

                    var args = me._getMacroArgs(macroString);

                    // If the macro can be run (because all values are non-null), add it to the list to execute.
                    if (args !== false && macroString) {
                        // Add all macros to evaluate
                        macroObjects.push({
                            name: val.name,
                            property: val.field,
                            macro: macroString,
                            arguments: args
                        });
                    }
                }
            }
        });
        if (macroObjects.length > 0) {
            var macroPrompts = [];
            macroObjects.forEach(function(macroObject) {
                var prompt = me._getPromptForName(macroObject.name);
                me._setPromptLoading(prompt, true);
                macroPrompts.push(prompt);
            });
            return MacroOps.execute(macroObjects)
                .done(function(data) {
                    for (var i = 0; i < data.length; i++) {
                        var promptToSet = me._getPromptForName(macroObjects[i].name);
                        if (data[i].successCount === 1) {
                            promptToSet.set(macroObjects[i].property.toLowerCase(), data[i].successful[0] === "true");
                        }
                    }
                })
                .always(function() {
                    macroPrompts.forEach(function(prompt) {
                        me._setPromptLoading(prompt, false);
                    });
                });
        } else {
            return $.Deferred().resolve().promise();
        }
    },

    _finishPromptBatch: $.noop,

    /**
     * Handles a prompt in the datasource having its value changed.
     * It will update all dependent prompts.
     * @param prompt The single prompt that must have dependent prompts calculated
     */
    _handlePromptChange: function(prompt) {
        var me = this;
        // Array of prompts names that the current prompt is responsible for executing.
        var arrResponsibilities = prompt.responsibilityMapping || [];

        // Run all prompts conditional properties that resolve when the prompt changes, executing a single call to the server
        var conditionalProps = this._changePromptBinding(arrResponsibilities);

        //Run all prompts return an array of promises that resolve when each prompt resolves
        var executing = arrResponsibilities.map(function(val) {
            // We moved to responsibilityMapping supporting a {name:[name],field:'VALUE'||'REQUIRED'||'DISABLED'||'HIDDEN'} structure,
            // but will still support a simple string array of field names as well.
            if (!val.field || val.field === "VALUE") {
                // Look for value in the new format. Otherwise, fall back to val being just a string.
                var fieldName = val;
                if (val.field === "VALUE") {
                    fieldName = val.name;
                }
                return me._runPrompt(fieldName);
            }
        });
        this._finishPromptBatch();
        return $.when.apply($, executing.concat(conditionalProps));
    }
});
